import { createAction, createGetter, on, select } from '../../../factories';
import * as RemoteCollectionStore from './remote-collection.store';
import { StoreFeature } from '../../../store';
import { SOFTLINE_STORE_ENTITY_SERVICE } from '../single/entity.shared';
import { Entity } from '../types/entity';
import { Dictionary } from '../../../../types/dictionary';
import { Patch } from '../types/patch';
import { handleSubscriptionState } from '../../remote/subscription.store';
import {
  ActionState,
  handleObservableActionState,
} from '../../remote/action.store';
import { SOFTLINE_SERVICE_UUID } from '../../../../core.shared';
import * as KeyStore from '../../remote/key.store';
import * as ParamsStore from '../../remote/params.store';
import { lastValueFrom } from 'rxjs';

export interface CreateActionParameters<T extends object = object> {
  entity: T;
  pathParams?: Dictionary<unknown>;
  token?: string;
}

export interface UpdateActionParameters<T extends Entity = Entity> {
  entity: T;
  pathParams?: Dictionary<unknown>;
  token?: string;
}

export interface PatchActionParameters<T extends Entity = Entity> {
  patch: Patch<T>;
  pathParams?: Dictionary<unknown>;
  token?: string;
}

export interface DeleteActionParameters<T extends Entity = Entity> {
  entity: T;
  pathParams?: Dictionary<unknown>;
  token?: string;
}

export interface State<T extends Entity = Entity>
  extends RemoteCollectionStore.State<T> {}

export class Store<T extends Entity = Entity> {
  mutations = {
    ...this.collectionStore.mutations,
  };

  getters = {
    ...this.collectionStore.getters,
    saving: createGetter<State<T>, boolean>('saving'),
    saved: createGetter<State<T>, boolean>('saved'),
  };

  actions = {
    ...this.collectionStore.actions,
    create: createAction<State, CreateActionParameters, T>('create'),
    patch: createAction<State, PatchActionParameters<T>, T>('patch'),
    update: createAction<State, UpdateActionParameters<T>, T>('update'),
    delete: createAction<State, DeleteActionParameters<T>, T>('delete'),
  };

  feature: StoreFeature<State<T>> = {
    initialState: {
      ...this.collectionStore.feature.initialState,
    },
    mutations: [...this.collectionStore.feature.mutations],
    getters: [
      ...this.collectionStore.feature.getters,
      select(this.getters.saving, ({ get, featureName, params }) => {
        const actions = [
          this.actions.create.name,
          this.actions.patch.name,
          this.actions.update.name,
          this.actions.delete.name,
        ];
        const states: ActionState[] = ['pending', 'processing'];
        return get(featureName, getters.action.hasState, {
          actions,
          states,
          id: params,
        });
      }),
      select(this.getters.saved, ({ get, featureName, params }) => {
        const actions = [
          this.actions.create.name,
          this.actions.patch.name,
          this.actions.update.name,
          this.actions.delete.name,
        ];
        const states: ActionState[] = ['succeeded', 'failed'];
        return get(featureName, getters.action.hasState, {
          actions,
          states,
          id: params,
        });
      }),
    ],
    actions: [
      ...this.collectionStore.feature.actions,
      on(
        this.actions.create,
        async ({ commit, featureName, params, injector }) => {
          const service = injector.get(SOFTLINE_STORE_ENTITY_SERVICE);
          const token = params.token ?? injector.get(SOFTLINE_SERVICE_UUID)();
          commit(featureName, KeyStore.mutations.add, token);
          commit(featureName, ParamsStore.mutations.add, {
            key: token,
            params,
          });

          const observable = service.create(params.entity, params.pathParams);
          const subscription$ = handleSubscriptionState(
            observable,
            featureName,
            commit,
            token
          );
          const result = await lastValueFrom(
            handleObservableActionState(
              subscription$,
              featureName,
              commit,
              this.actions.create.name,
              token
            )
          );
          commit(featureName, this.mutations.add, result);
          return result;
        }
      ),
      on(
        this.actions.patch,
        async ({ commit, featureName, params, injector }) => {
          const service = injector.get(SOFTLINE_STORE_ENTITY_SERVICE);
          const token = params.token ?? injector.get(SOFTLINE_SERVICE_UUID)();
          commit(featureName, KeyStore.mutations.add, token);
          commit(featureName, ParamsStore.mutations.add, {
            key: token,
            params,
          });

          const observable = service.patch(params.patch, params.pathParams);
          const subscription$ = handleSubscriptionState(
            observable,
            featureName,
            commit,
            token
          );
          const result = await lastValueFrom(
            handleObservableActionState(
              subscription$,
              featureName,
              commit,
              this.actions.patch.name,
              token
            )
          );
          commit(featureName, this.mutations.update, result);
          return result;
        }
      ),
      on(
        this.actions.update,
        async ({ commit, featureName, params, injector }) => {
          const service = injector.get(SOFTLINE_STORE_ENTITY_SERVICE);
          const token = params.token ?? injector.get(SOFTLINE_SERVICE_UUID)();
          commit(featureName, KeyStore.mutations.add, token);
          commit(featureName, ParamsStore.mutations.add, {
            key: token,
            params,
          });

          const observable = service.update(params.entity, params.pathParams);
          const subscription$ = handleSubscriptionState(
            observable,
            featureName,
            commit,
            token
          );
          const result = await lastValueFrom(
            handleObservableActionState(
              subscription$,
              featureName,
              commit,
              this.actions.update.name,
              token
            )
          );
          commit(featureName, this.mutations.update, result);
          return result;
        }
      ),
      on(
        this.actions.delete,
        async ({ commit, featureName, params, injector }) => {
          const service = injector.get(SOFTLINE_STORE_ENTITY_SERVICE);
          const token = params.token ?? injector.get(SOFTLINE_SERVICE_UUID)();
          commit(featureName, KeyStore.mutations.add, token);
          commit(featureName, ParamsStore.mutations.add, {
            key: token,
            params,
          });

          const observable = service.delete(params.entity, params.pathParams);
          const subscription$ = handleSubscriptionState(
            observable,
            featureName,
            commit,
            token
          );
          const result = await lastValueFrom(
            handleObservableActionState(
              subscription$,
              featureName,
              commit,
              this.actions.delete.name,
              token
            )
          );
          commit(featureName, this.mutations.remove, result);
          return result;
        }
      ),
    ],
  };

  constructor(private collectionStore: RemoteCollectionStore.Store<T>) {}
}

export function create<T extends Entity>(): Store<T> {
  const collectionStore = RemoteCollectionStore.create<T>();
  return new Store<T>(collectionStore);
}

const instance = create();
export const mutations = instance.mutations;
export const getters = instance.getters;
export const actions = instance.actions;
export const feature = instance.feature;
